import type { Editor as ToastEditorInstance } from '@toast-ui/editor'

/**
 * Composable for handling ToastUI editor widget rules and serialization
 * for mustache-style parameters ({{param}}, {{#param}}, {{/param}})
 */

export interface WidgetOptions {
    onRemove?: () => void
}

/**
 * Creates widget rules configuration for ToastUI editor
 * Converts {{param}}, {{#param}}, {{/param}} into styled chips
 */
export function createWidgetRules (options: WidgetOptions = {}) {
    return {
        widgetRules: [
            {
                rule: /\{\{[#/]?[^}]+\}\}/,
                toDOM (text: string): HTMLElement {
                    // Extract the parameter text (including # or / prefix if present)
                    const match = text.match(/\{\{([#/]?)([^}]+)\}\}/)
                    const prefix = match?.[1] || ''
                    const paramName = match?.[2] || text

                    const chip = document.createElement('span')
                    chip.className = 'parameter-chip'
                    chip.setAttribute('contenteditable', 'false')
                    chip.setAttribute('data-text', text) // Store original text for serialization

                    const prefixSpan = document.createElement('span')
                    prefixSpan.className = 'parameter-chip__prefix'
                    if (prefix === '#') {
                        prefixSpan.textContent = '#'
                        chip.classList.add('parameter-chip--open-if')
                    } else if (prefix === '/') {
                        prefixSpan.textContent = '/'
                        chip.classList.add('parameter-chip--close-if')
                    }

                    const label = document.createElement('span')
                    label.className = 'parameter-chip__label'
                    label.textContent = paramName

                    const removeBtn = document.createElement('span')
                    removeBtn.className = 'parameter-chip__remove'
                    removeBtn.textContent = '×'
                    removeBtn.onclick = (e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        // Remove the entire widget wrapper (tui-widget), not just our chip
                        // The widget wrapper is the parent of our chip
                        const widgetWrapper = chip.closest('.tui-widget') || chip.parentElement
                        if (widgetWrapper) {
                            widgetWrapper.remove()
                        } else {
                            chip.remove()
                        }
                        options.onRemove?.()
                    }

                    if (prefix) {
                        chip.appendChild(prefixSpan)
                    }
                    chip.appendChild(label)
                    chip.appendChild(removeBtn)

                    return chip
                }
            }
        ]
    }
}

/**
 * Extracts markdown value from editor, replacing widget placeholders
 * with original parameter text
 */
export function getEditorMarkdown (editor: ToastEditorInstance): string {
    const  markdown = editor.getMarkdown()

    // Collect all parameter chips and their original text in order
    // const wwContainer = editor.getEditorElements?.()?.wwEditor
    // const chipTexts: string[] = []
    //
    // if (wwContainer) {
    //     const chips = wwContainer.querySelectorAll('.parameter-chip[data-text]')
    //     chips.forEach((chip) => {
    //         const originalText = chip.getAttribute('data-text')
    //         if (originalText) {
    //             chipTexts.push(originalText)
    //         }
    //     })
    // }

    return stripToastUiWidgets(markdown)
}

/**
 * Builds a parameter placeholder based on type
 */
export type ParameterType = 'value' | 'openIf' | 'closeIf'

export function buildParameterPlaceholder (paramValue: string, type: ParameterType = 'value'): string {
    switch (type) {
        case 'openIf':
            return `{{#${paramValue}}}`
        case 'closeIf':
            return `{{/${paramValue}}}`
        case 'value':
        default:
            return `{{${paramValue}}}`
    }
}

export function stripToastUiWidgets (markdown: string): string {
    if (!markdown) return markdown

    return markdown.replace(
        /\$\$widget\d+\s*([\s\S]*?)\$\$/g,
        '$1'
    )
}
